import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context";

// Module-level cache — fetched once per page session, survives modal close/reopen
let cachedIssueOptions = null;

const FeedbackModal = ({ target, onClose, onSubmitted }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { aiChatBaseUrl, aiChatAuth } = useContext(AuthContext);
    // Allow toggling between positive/negative inside the modal
    const [isPositive, setIsPositive] = useState(target.reaction === "up");

    const [issueOptions, setIssueOptions] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    useEffect(() => {
        if (isPositive) return;
        if (cachedIssueOptions) {
            setIssueOptions(cachedIssueOptions);
            return;
        }
        setOptionsLoading(true);
        fetch(`${aiChatBaseUrl}/feedback/issue-types`, { headers: { Authorization: aiChatAuth } })
            .then((r) => r.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
                const mapped = list.map((item) =>
                    typeof item === "string"
                        ? { id: item, label: item }
                        : { id: item.id ?? item.issue_type_id, label: item.label || item.name || String(item.id) }
                );
                cachedIssueOptions = mapped;
                setIssueOptions(mapped);
            })
            .catch(() => setIssueOptions([]))
            .finally(() => setOptionsLoading(false));
    }, [isPositive]);

    const existing = target.existingFeedback;

    // Pre-fill from existing feedback if same polarity
    const [issueTypeId, setIssueTypeId] = useState(() => {
        if (existing && !existing.is_positive && existing.issue_type?.id) {
            return String(existing.issue_type.id);
        }
        return "";
    });
    const [comment, setComment] = useState(existing?.comment || "");
    const [submitting, setSubmitting] = useState(false);
    const [issueError, setIssueError] = useState(false);

    // Reset issue when toggling polarity
    const handleTogglePolarity = (positive) => {
        setIsPositive(positive);
        setIssueTypeId("");
        setIssueError(false);
        if (positive) {
            setComment(existing?.is_positive ? (existing?.comment || "") : "");
        } else {
            setComment(!existing?.is_positive ? (existing?.comment || "") : "");
        }
    };

    const chatId = parseInt(String(target.chatId).replace("msg-", ""), 10) || null;

    const handleSubmit = async () => {
        if (!isPositive && !issueTypeId) {
            setIssueError(true);
            return;
        }
        setSubmitting(true);
        try {
            await fetch(`${aiChatBaseUrl}/feedback`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: aiChatAuth },
                body: JSON.stringify({
                    chat_id: chatId,
                    session_id: target.sessionId,
                    is_positive: isPositive,
                    issue_type_id: isPositive ? null : Number(issueTypeId),
                    comment: comment.trim() || null,
                }),
            });
        } catch {
            // proceed regardless
        } finally {
            setSubmitting(false);
            onSubmitted(target.messageId, isPositive ? "up" : "down");
        }
    };

    const handleDetailedFeedback = () => {
        const params = new URLSearchParams();
        params.set("session_id", target.sessionId || "");
        if (chatId) params.set("chat_id", chatId);
        onClose();
        navigate(`/aura/feedback?${params.toString()}`);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4
        bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.08),transparent_70%)] backdrop-blur-sm">

            <div className="absolute w-80 h-80 rounded-full bg-indigo-300/20 blur-3xl pointer-events-none" />

            <div className="relative w-full max-w-md">
                <div className="rounded-[28px] border border-white/70 bg-white/60 backdrop-blur-xl
                shadow-[0_26px_90px_rgba(79,70,229,0.22)] overflow-hidden">

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 pt-5 pb-4
                    bg-gradient-to-r from-indigo-50/70 to-violet-50/50 border-b border-white/60">
                        <p className="text-sm font-semibold text-slate-800">{t('aura feedback title')}</p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-white/60"
                            aria-label="Close"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="px-6 py-5 space-y-4">

                        {/* Positive / Negative toggle */}
                        <div className="flex rounded-[14px] border border-white/60 bg-white/40 p-1 gap-1">
                            <button
                                type="button"
                                onClick={() => handleTogglePolarity(true)}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[10px] text-xs font-medium transition
                                ${isPositive
                                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm"
                                    : "text-slate-500 hover:bg-white/60"
                                }`}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill={isPositive ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8">
                                    <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3z" />
                                    <path d="M7 11l4-7a2 2 0 0 1 2-1h1a2 2 0 0 1 2 2v3h3a2 2 0 0 1 2 2l-2 7a2 2 0 0 1-2 2H7" />
                                </svg>
                                {t('aura feedback positive')}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleTogglePolarity(false)}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[10px] text-xs font-medium transition
                                ${!isPositive
                                    ? "bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-sm"
                                    : "text-slate-500 hover:bg-white/60"
                                }`}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill={!isPositive ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8">
                                    <path d="M17 13V4h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3z" />
                                    <path d="M17 13l-4 7a2 2 0 0 1-2 1h-1a2 2 0 0 1-2-2v-3H5a2 2 0 0 1-2-2l2-7a2 2 0 0 1 2-2h10" />
                                </svg>
                                {t('aura feedback negative')}
                            </button>
                        </div>

                        {/* Negative: mandatory dropdown */}
                        {!isPositive && (
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                                    {t('aura feedback issue label')}
                                    <span className="text-rose-500 ml-0.5">*</span>
                                </label>
                                <select
                                    value={issueTypeId}
                                    onChange={(e) => { setIssueTypeId(e.target.value); setIssueError(false); }}
                                    disabled={optionsLoading}
                                    className={`w-full rounded-[14px] border bg-white/70 backdrop-blur px-3 py-2.5
                                    text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300
                                    shadow-[0_8px_20px_rgba(15,23,42,0.06)] disabled:opacity-60
                                    ${issueError ? "border-rose-400 ring-1 ring-rose-300" : "border-white/60"}`}
                                >
                                    <option value="">{optionsLoading ? t('aura loading') : t('aura feedback select')}</option>
                                    {issueOptions.map((opt) => (
                                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                                    ))}
                                </select>
                                {issueError && (
                                    <p className="text-[11px] text-rose-500 mt-1">{t('aura feedback issue error')}</p>
                                )}
                            </div>
                        )}

                        {/* Comment */}
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1.5">
                                {t('aura feedback comment label')}{" "}
                                <span className="text-slate-400 font-normal">(optional)</span>
                            </label>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder={
                                    isPositive
                                        ? t('aura feedback comment positive')
                                        : t('aura feedback comment negative')
                                }
                                rows={3}
                                className="w-full rounded-[14px] border border-white/60 bg-white/70 backdrop-blur
                                px-3 py-2.5 text-sm text-slate-700 resize-none placeholder:text-slate-400
                                focus:outline-none focus:ring-2 focus:ring-indigo-300
                                shadow-[0_8px_20px_rgba(15,23,42,0.06)]"
                            />
                        </div>

                        {/* Detailed feedback link */}
                        <p className="text-[11px] text-slate-400">
                            {t('aura feedback hint')}{" "}
                            <button
                                type="button"
                                onClick={handleDetailedFeedback}
                                className="text-indigo-500 hover:text-indigo-700 font-medium underline"
                            >
                                {t('aura feedback detail link')}
                            </button>
                        </p>

                        {/* Actions */}
                        <div className="flex items-center justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 rounded-[12px] text-sm text-slate-500
                                border border-white/60 bg-white/50 hover:bg-white/80 backdrop-blur"
                            >
                                {t('aura feedback cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="px-5 py-2 rounded-[12px] text-sm font-semibold text-white
                                bg-gradient-to-r from-indigo-600 to-violet-600
                                shadow-[0_12px_30px_rgba(99,102,241,0.35)] hover:opacity-90 disabled:opacity-50"
                            >
                                {submitting ? t('aura feedback submitting') : existing ? t('aura feedback update') : t('aura feedback submit')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;
